/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LOS MONTOS DE LOS BONOS — LO QUE LA APP PROMETE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️⚠️ ESTE ARCHIVO ES UN ESPEJO DE `functions/src/incentives.ts`.
 * Quien PAGA es el servidor. Esto es solo lo que se le ENSEÑA al chofer, y
 * tiene que decir exactamente lo mismo. Si cambias un número allá, cámbialo
 * aquí EL MISMO DÍA.
 *
 * ── POR QUÉ EXISTE, Y POR QUÉ NO BASTA CON «TENER CUIDADO» ───────────────
 *
 * Esto ya se rompió DOS VECES, y las dos del mismo modo: la tabla estaba
 * escrita a mano dentro de la pantalla.
 *
 *   1ª — La pantalla decía RD$400 de meta mensual mientras el servidor pagaba
 *        RD$500. Se corrigió a mano, dejando el aviso «deben coincidir SIEMPRE»
 *        en un comentario. Un comentario no impide nada.
 *
 *   2ª — La encontró el barrido del 4-oct-2026, y era peor porque tocaba a
 *        quien menos puede permitírselo. La pantalla calculaba el bono de
 *        referidos con `isMotorcycle ? 800 : 1000`, y el servidor con una tabla
 *        de cuatro vehículos. Dos fallos encadenados:
 *
 *          · `profile.tsx` montaba el modal con `vehicleType="CAR"` ESCRITO A
 *            MANO, así que a TODO chofer se le prometían RD$1,000.
 *          · Y aunque se le hubiera pasado el vehículo real, la BICICLETA no
 *            estaba contemplada: el servidor le paga 800 y la pantalla le
 *            habría dicho 1,000 igual. El propio tipo de la prop decía
 *            `'CAR' | 'MOTORCYCLE' | 'MINIBUS'` — el hueco estaba hasta ahí.
 *
 *        Resultado para un motorista: leía «Gana RD$1,000.00 por cada 3
 *        choferes que invites», invitaba a tres, y le entraban 800. RD$200
 *        menos de lo prometido, cada tres invitados, sin explicación.
 *
 * Una tabla escrita dos veces se desincroniza; la pregunta es cuándo. Aquí al
 * menos está en UN sitio del lado de la app, con la misma forma que la del
 * servidor —un mapa por vehículo con su valor por defecto, no un booleano—
 * para que al compararlas se vea de un vistazo si cuadran.
 *
 * ── LA FORMA CORRECTA, EL DÍA QUE HAYA TIEMPO ────────────────────────────
 *
 * Que estos montos vivan en `config/plataforma` de Firestore y los lean los
 * dos lados. El proyecto ya usa ese patrón para otras cifras. Mientras tanto,
 * esto es mejor que un número suelto dentro del JSX.
 */

/**
 * Bono por cada 3 referidos que ya pagan.
 * Espejo de `REFERRAL_BONUS_BY_VEHICLE` en functions/src/incentives.ts.
 */
export const BONO_REFERIDO_POR_VEHICULO: Record<string, number> = {
  MOTORCYCLE: 800,
  BICYCLE: 800,
  CAR: 1000,
  MINIBUS: 1000,
};

/** Espejo de `REFERRAL_BONUS_DEFAULT`. Lo que se paga sin vehículo conocido. */
export const BONO_REFERIDO_POR_DEFECTO = 1000;

/** Espejo de `REFERRALS_PER_BONUS`. */
export const REFERIDOS_POR_BONO = 3;

/** Espejo de `MONTHLY_GOAL_TRIPS` y `MONTHLY_GOAL_BONUS`. */
export const META_MENSUAL_VIAJES = 250;
export const META_MENSUAL_BONO = 500;

/** Espejo de `LIFETIME_MILESTONE_TRIPS` y `LIFETIME_MILESTONE_BONUS`. */
export const HITO_VIAJES = 1500;
export const HITO_BONO = 2000;

/**
 * El bono que le toca a un vehículo.
 *
 * Misma lógica que `referralBonusFor` del servidor: se busca en la tabla y, si
 * el vehículo no se conoce, se cae al valor por defecto. Nunca se inventa un
 * número distinto del que se va a pagar.
 */
export function bonoDeReferidoPara(vehiculo?: string | null): number {
  const clave = String(vehiculo ?? '').trim().toUpperCase();
  return BONO_REFERIDO_POR_VEHICULO[clave] ?? BONO_REFERIDO_POR_DEFECTO;
}
